import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { KeycloakAuthService } from "./keycloak-auth.service";

export type AppRole = "Admin" | "Helpdesk_L1" | "Technical_Support_L2";

@Injectable()
export class RbacService {
    constructor(private keycloakAuthService: KeycloakAuthService) {
    }

    isAdmin(): boolean {
        return this.keycloakAuthService.hasRole("Admin");
    }

    canManageTenants(): boolean {
        return this.allowedFor();
    }

    canManageServices(): boolean {
        return this.allowedFor();
    }

    canManageEmailModels(): boolean {
        return this.allowedFor();
    }

    canManageTenantEmailTemplates(): boolean {
        return this.canManageEmailModels();
    }

    canManageEmailSenders(): boolean {
        return this.canManageEmailModels();
    }

    canDeletePolicies(): boolean {
        return this.allowedFor();
    }

    canManagePolicies(): boolean {
        return this.allowedFor("Technical_Support_L2");
    }

    canManageUserGroups(): boolean {
        return this.allowedFor();
    }

    canDeleteLocationGroups(): boolean {
        return this.allowedFor();
    }

    canManageLocationGroups(): boolean {
        return this.allowedFor("Technical_Support_L2");
    }

    canDeleteLocations(): boolean {
        return this.allowedFor();
    }

    canManageLocations(): boolean {
        return this.allowedFor("Technical_Support_L2");
    }

    canManageNetworkDevices(): boolean {
        return this.allowedFor("Technical_Support_L2");
    }

    canDeleteLocationDetails(): boolean {
        return this.canDeleteLocations();
    }

    canReadTenantRadiusClients(): boolean {
        return this.allowedFor("Technical_Support_L2");
    }

    canManageRadius(): boolean {
        return this.allowedFor();
    }

    canManageVendors(): boolean {
        return this.allowedFor();
    }

    canManageCaptivePortals(): boolean {
        return this.allowedFor("Technical_Support_L2");
    }

    canDeleteCaptivePortals(): boolean {
        return this.allowedFor();
    }

    canManagePages(): boolean {
        return this.allowedFor();
    }

    canManageMacAddresses(): boolean {
        return this.allowedFor();
    }

    canDeleteMacAddresses(): boolean {
        return this.allowedFor("Technical_Support_L2");
    }

    canAccessRoute(url: string): boolean {
        if (!this.keycloakAuthService.isEnabled()) {
            return true;
        }

        let path = this.normalizePath(url);
        if (path === "/" || path === "/index") {
            return this.isAdmin();
        }

        if (path.indexOf("/analytics") === 0) {
            return false;
        }

        if (this.isAdmin()) {
            return true;
        }

        if (this.hasRole("Technical_Support_L2")) {
            return !this.startsWithAny(path, [
                "/administration/strategies",
                "/captive-portal/strategies",
                "/editor"
            ]);
        }

        if (this.hasRole("Helpdesk_L1")) {
            if (/^\/administration\/tenants\/[^/]+\/services\/.+/.test(path)) {
                return false;
            }
            return !this.startsWithAny(path, [
                "/administration/tenants/" + this.tenantPathSegment(path) + "/email-templates",
                "/administration/channel",
                "/administration/email-models",
                "/administration/email-senders",
                "/administration/radius",
                "/administration/strategies",
                "/administration/create-location",
                "/captive-portal/strategies",
                "/editor"
            ]);
        }

        return false;
    }

    firstAllowedRoute(): string {
        return this.isAdmin() ? "/index" : "/administration/tenants";
    }

    canRequest(method: string, url: string): boolean {
        if (!this.keycloakAuthService.isEnabled()) {
            return true;
        }

        if (this.isAdmin()) {
            return true;
        }

        let verb = (method || "GET").toUpperCase();
        let path = this.apiPath(url);
        if (this.isAnalytics(url) || /\/settings(?:[?#]|$)/.test(url)) {
            return false;
        }
        if (!path) {
            return true;
        }

        // Le operazioni speciali S verranno definite in una fase successiva.
        if (this.isSpecialOperation(verb, path)) {
            return true;
        }

        let role = this.currentRole();
        if (!role) {
            return false;
        }

        if (/^tenants\/[^/]+\/radius-clients(?:\/[^/]+)?$/.test(path)) {
            return verb === "GET" && role === "Technical_Support_L2";
        }
        if (/^services\/[^/]+\/channels(?:\/[^/]+)?$/.test(path)) {
            return verb === "GET" && role === "Technical_Support_L2";
        }
        if (/^tenants(?:\/[^/]+)?$/.test(path)) {
            return verb === "GET";
        }
        if (/^tenants\/[^/]+\/services(?:\/.*)?$/.test(path)) {
            if (/\/channels(?:\/.*)?$/.test(path)) {
                return verb === "GET" && role === "Technical_Support_L2";
            }
            return verb === "GET";
        }
        if (/^(calendar-bot-accounts)(?:\/.*)?$/.test(path) || /\/analytical-groups(?:\/.*)?$/.test(path)) {
            return verb === "GET" && role === "Technical_Support_L2";
        }

        if (/^email-models(?:\/.*)?$/.test(path) || /^(email-senders|senders)(?:\/.*)?$/.test(path) || /\/email-template(?:\/.*)?$/.test(path)) {
            return verb === "GET" && role === "Technical_Support_L2";
        }

        if (/^policy(?:\/.*)?$/.test(path)) {
            if (verb === "GET") { return true; }
            if (verb === "DELETE") { return false; }
            return role === "Technical_Support_L2";
        }
        if (/^user_group(?:\/.*)?$/.test(path)) {
            return verb === "GET";
        }
        if (/^location_group(?:\/.*)?$/.test(path)) {
            return verb === "GET" || role === "Technical_Support_L2" && verb !== "DELETE";
        }
        if (/^locations-monitoring-settings(?:\/.*)?$/.test(path)) {
            if (verb === "GET") { return true; }
            return role === "Technical_Support_L2";
        }

        if (/^location(?:\?.*)?$/.test(path)) {
            return verb === "GET" || role === "Technical_Support_L2";
        }
        if (/^location\/[^/]+$/.test(path)) {
            return verb === "GET";
        }
        if (/^location-info(?:\/.*)?$/.test(path)) {
            if (verb === "GET") { return true; }
            return role === "Technical_Support_L2" && verb !== "DELETE";
        }
        if (/^network_device(?:\/.*)?$/.test(path)) {
            return verb === "GET" || role === "Technical_Support_L2";
        }
        if (/^network_interface(?:\/.*)?$/.test(path)) {
            return verb === "GET" || role === "Technical_Support_L2";
        }
        if (/^network_devices_interfaces(?:\/.*)?$/.test(path)) {
            return verb === "GET";
        }

        if (/^radius(?:\/.*)?$/.test(path)) {
            return verb === "GET" && role === "Technical_Support_L2";
        }
        if (/^vendors(?:\/.*)?$/.test(path)) {
            return verb === "GET";
        }
        if (/^captive_portal(?:\/.*)?$/.test(path)) {
            if (verb === "GET") { return true; }
            return role === "Technical_Support_L2" && verb !== "DELETE";
        }
        if (/^pages(?:\/.*)?$/.test(path)) {
            return verb === "GET";
        }
        if (/^(contents|assets)(?:\/.*)?$/.test(path)) {
            return false;
        }
        if (/^configuration(?:\/.*)?$/.test(path) || /^strategy(?:\/.*)?$/.test(path)) {
            return verb === "GET";
        }
        if (/^strategy-config\/[^/]+\/[^/]+\/params$/.test(path)) {
            return verb === "GET";
        }
        if (/^strategy-config(?:\/.*)?$/.test(path)) {
            return false;
        }

        if (/^mac(?:\/.*)?$/.test(path)) {
            if (verb === "GET") { return true; }
            return role === "Technical_Support_L2" && verb === "DELETE";
        }
        if (/^operations(?:\/.*)?$/.test(path)) {
            return verb === "GET";
        }

        return true;
    }

    private currentRole(): AppRole {
        if (this.hasRole("Technical_Support_L2")) { return "Technical_Support_L2"; }
        if (this.hasRole("Helpdesk_L1")) { return "Helpdesk_L1"; }
        return null;
    }

    private hasRole(role: AppRole): boolean {
        return this.keycloakAuthService.hasRole(role);
    }

    // Admin può sempre tutto; gli altri ruoli solo se elencati.
    private allowedFor(...roles: AppRole[]): boolean {
        if (!this.keycloakAuthService.isEnabled() || this.isAdmin()) {
            return true;
        }
        return roles.some((role: AppRole) => this.hasRole(role));
    }

    private isSpecialOperation(verb: string, path: string): boolean {
        return /(^|\/)sync\//.test(path) || verb === "POST" && /network_devices_interfaces\/bulk$/.test(path);
    }

    private isAnalytics(url: string): boolean {
        let analyticsUrl = environment.ConfigEndpoints["analytics_url"] || "";
        return !!analyticsUrl && url.indexOf(analyticsUrl) === 0;
    }

    private apiPath(url: string): string {
        let clean = (url || "").split("#")[0];
        let endpoints: string[] = [];
        let config: any = environment.ConfigEndpoints || {};
        let directResources: any = {
            captive_vendors: "vendors",
            configuration_mac: "mac",
            monitoring_sessions: "operations",
            captive_config: "strategy-config"
        };
        Object.keys(config).forEach((key: string) => {
            if (config[key]) { endpoints.push(config[key]); }
        });
        endpoints.sort((a: string, b: string) => b.length - a.length);
        for (let endpoint of endpoints) {
            if (clean.indexOf(endpoint) === 0) {
                let key = Object.keys(config).find((name: string) => config[name] === endpoint);
                let suffix = clean.substring(endpoint.length).replace(/^\/+/, "").split("?")[0];
                let prefix = directResources[key];
                return prefix ? prefix + (suffix ? "/" + suffix : "") : suffix;
            }
        }
        return "";
    }

    private normalizePath(url: string): string {
        let path = (url || "/").split("?")[0].split("#")[0];
        return path.charAt(0) === "/" ? path : "/" + path;
    }

    private startsWithAny(path: string, prefixes: string[]): boolean {
        return prefixes.some((prefix: string) => path.indexOf(prefix) === 0);
    }

    private tenantPathSegment(path: string): string {
        let match = path.match(/^\/administration\/tenants\/([^/]+)/);
        return match ? match[1] : "__no_tenant__";
    }
}
